import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderForm } from 'models/order-form';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import OrderFormDetails from '../shared/order-form-details.component';
import { orderFormTemplate } from 'apollo/templates/order-form';
import ConstructionSummary from './construction-summary';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import { useNavigate } from 'react-router-dom';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type OrderFormDialogProps = {
    open: boolean;
    onClose: () => void;
    partialOrderForm: Partial<OrderForm> | undefined;
};
const OrderFormDialog: FC<OrderFormDialogProps> = ({ open, onClose, partialOrderForm }: OrderFormDialogProps) => {
    const { t: translate } = useTranslation(['order-forms', 'common', 'orders']);
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const [form, setForm] = useState<OrderForm>();
    const [isLoading, setIsLoading] = useState(false);

    const [orderForm] = useLazyQuery(orderFormTemplate, {
        onCompleted: ({ orderForm }) => {
            setForm(orderForm);
            setIsLoading(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setIsLoading(true);
        setForm(undefined);
        if (partialOrderForm) {
            orderForm({ variables: { _id: partialOrderForm?._id } });
        }
    }, [partialOrderForm]);

    const createDenthubOrder = () => {
        if (form) {
            navigate(`/orders/create-from-order-form/${form._id}`);
        }
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={onClose}
            title={form?.title}
            actions={
                <Button onClick={() => createDenthubOrder()} data-test="order-form-dialog.create-order-btn">
                    {translate('order-forms:CREATE_DENTHUB_ORDER')}
                </Button>
            }
        >
            {isLoading ? <CircularProgress /> : undefined}
            {form ? (
                <Fragment>
                    <Box display="flex">
                        <Box mr={2} sx={{ width: '100%' }}>
                            <Box mb={2}>
                                <AiAlert
                                    title={translate('common:AI_ASSISTANT')}
                                    message={translate('AI_FOUND_INFO_ON_ORDER_FORM')}
                                />
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <strong>{translate('orders:DELIVERY')}</strong>
                                            </TableCell>
                                            <TableCell>
                                                {form.deliveryDate ? (
                                                    <Box>
                                                        <span>{formatDate(form.deliveryDate, 'date')}</span>
                                                        {form.deliveryTime ? (
                                                            <span>
                                                                {' '}
                                                                {form.deliveryTime.hour.toString().padStart(2, '0')}:
                                                                {form.deliveryTime.minute.toString().padStart(2, '0')}
                                                            </span>
                                                        ) : undefined}
                                                    </Box>
                                                ) : (
                                                    <Box>{translate('order-forms:NO_DELIVERY_DATE')}</Box>
                                                )}
                                            </TableCell>
                                        </TableRow>

                                        {form.formFields?.patientFirstName.value ||
                                        form.formFields?.patientLastName.value ? (
                                            <TableRow>
                                                <TableCell sx={{ width: 180 }}>
                                                    <strong>{translate('common:PATIENT')}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {form.formFields?.patientFirstName.value}{' '}
                                                    {form.formFields?.patientLastName.value}
                                                </TableCell>
                                            </TableRow>
                                        ) : undefined}

                                        {form.formFields?.patientPersonalIdentityNumber.value ? (
                                            <TableRow>
                                                <TableCell>
                                                    <strong>{translate('common:PERSONAL_IDENTITY_NUMBER')}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {form.formFields?.patientPersonalIdentityNumber.value}
                                                </TableCell>
                                            </TableRow>
                                        ) : undefined}

                                        {form.formFields?.dentistFirstName.value ||
                                        form.formFields?.dentistLastName.value ? (
                                            <TableRow>
                                                <TableCell>
                                                    <strong>{translate('common:DENTIST')}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {form.formFields?.dentistFirstName.value}{' '}
                                                    {form.formFields?.dentistLastName.value}
                                                </TableCell>
                                            </TableRow>
                                        ) : undefined}

                                        {form.formFields?.clinicName.value ? (
                                            <TableRow>
                                                <TableCell>
                                                    <strong>{translate('common:CLINIC')}</strong>
                                                </TableCell>
                                                <TableCell>{form.formFields?.clinicName.value}</TableCell>
                                            </TableRow>
                                        ) : undefined}

                                        {form.formFields?.constructionComment.value ? (
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <strong>{translate('common:COMMENTS')}</strong>
                                                </TableCell>
                                                {/* The reader writes one item per line (DD-3814) — keep the line breaks. */}
                                                <TableCell sx={{ whiteSpace: 'pre-line' }}>
                                                    {form.formFields?.constructionComment.value}
                                                </TableCell>
                                            </TableRow>
                                        ) : undefined}

                                        {form.formFields?.constructionColor.value ? (
                                            <TableRow>
                                                <TableCell>
                                                    <strong>{translate('orders:COLOR')}</strong>
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                                    {form.formFields?.constructionColor.value}
                                                </TableCell>
                                            </TableRow>
                                        ) : undefined}

                                        {form.restorationData.length > 0 ? (
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <strong>{translate('orders:CONSTRUCTIONS')}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <ConstructionSummary restorationData={form.restorationData} />
                                                </TableCell>
                                            </TableRow>
                                        ) : undefined}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <Box sx={{ height: '70vh', marginBottom: '50px' }}>
                            {/* the dialog (maxWidth lg) minus the info panel on the left */}
                            <OrderFormDetails
                                orderForm={form}
                                height="70vh"
                                maxImageWidth="calc(min(1200px, 100vw - 64px) - 400px)"
                            />
                        </Box>
                    </Box>
                </Fragment>
            ) : undefined}
        </DHDialog>
    );
};

export default OrderFormDialog;
